import './ScenarioPlanningXAxisTick.scss';
import * as R from 'ramda';
import { ReactElement } from 'react';
import { ComposedChart, Line, YAxis } from 'recharts';
import { PercentChange } from '@Components/PercentChange';
import { DcDgBadge } from '@Components/DcDgBadge/DcDgBadge';
import {
  buildSparkChartData,
  computeDeltaPercent,
  FLAT_LINE_VAL,
  parseChannelId,
  SCENARIO_NEGATIVE_PILL_COLOR,
  ScenarioPlanningChartEntry,
  SPARK_CHART_DATA_KEY,
  SPARKCHART_LINE_WIDTH
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { Size, StretchMode } from '@Components/designConstants';

const FONT_SIZE_MAP = {
  channel: 14,
  platform: 14,
  tactic: 12
};
const LINE_HEIGHT_PADDING = 3;
const TEXT_CONTAINER_HEIGHT_MAP = {
  channel: FONT_SIZE_MAP.channel + LINE_HEIGHT_PADDING,
  platform: FONT_SIZE_MAP.platform + LINE_HEIGHT_PADDING,
  tactic: FONT_SIZE_MAP.tactic + LINE_HEIGHT_PADDING
};
const PERCENT_CHANGE_HEIGHT = 20;
const SPARK_CHART_HEIGHT = 25;
const TEXT_CONTAINER_GAP = 2;
const TICK_FLEX_GAP = 4;
// Compensates for the tickSize+tickMargin we zero out on the parent <XAxis> so
// the foreignObject sits flush with the axis line; this adds the equivalent
// breathing room back as padding above the channel text.
const TICK_TOP_PADDING = 8;

const getTextContainerHeight = (granularity: number): number => {
  let height = TEXT_CONTAINER_HEIGHT_MAP.channel;
  if (granularity === 2) {
    height += TEXT_CONTAINER_HEIGHT_MAP.platform + TEXT_CONTAINER_GAP;
  } else if (granularity === 3) {
    height +=
      TEXT_CONTAINER_HEIGHT_MAP.platform +
      TEXT_CONTAINER_HEIGHT_MAP.tactic +
      2 * TEXT_CONTAINER_GAP;
  }
  return height;
};

export const getTickHeight = (granularity: number, hasScenarioChange?: boolean): number => {
  let height =
    TICK_TOP_PADDING + SPARK_CHART_HEIGHT + TICK_FLEX_GAP + getTextContainerHeight(granularity);
  if (hasScenarioChange) {
    height += PERCENT_CHANGE_HEIGHT + TICK_FLEX_GAP;
  }
  return height;
};

interface ScenarioPlanningXAxisTickProps {
  chartData: ScenarioPlanningChartEntry[];
  granularity: number;
  height: number;
  payload: { index: number };
  width: number;
  x: number;
  y: number;
}

export const ScenarioPlanningXAxisTick = (
  props: ScenarioPlanningXAxisTickProps
): ReactElement | null => {
  const { chartData, granularity, height, payload, width, x, y } = props;
  if (R.isNil(chartData) || R.isNil(chartData[payload.index])) {
    return null;
  }
  const { index } = payload;
  const currentItem = chartData[index];
  const {
    baselineValue,
    color,
    directValue,
    id,
    hasScenario,
    isDemandCapture,
    flightingData,
    metric,
    actualizedValue,
    showActualized,
    useHistoricalFlighting
  } = currentItem;
  const { channel, platform, tactic } = parseChannelId(id ?? '');
  const xPos = x - width / 2;
  const textContainer = (
    <div
      className='text-container'
      style={{
        height: getTextContainerHeight(granularity),
        minHeight: getTextContainerHeight(granularity),
        width
      }}
    >
      <div className='channel' style={{ width }}>
        <div className='channel-text'>{channel}</div>
        {isDemandCapture !== undefined && isDemandCapture !== null && (
          <DcDgBadge isDemandCapture={isDemandCapture} />
        )}
      </div>
      {granularity >= 2 && (
        <div
          className='platform'
          style={{
            visibility: platform && R.toLower(platform) !== 'n/a' ? 'visible' : 'hidden',
            width
          }}
        >
          {platform}
        </div>
      )}
      {granularity >= 3 && (
        <div
          className='tactic'
          style={{
            visibility: tactic && R.toLower(tactic) !== 'n/a' ? 'visible' : 'hidden',
            width
          }}
        >
          {tactic}
        </div>
      )}
    </div>
  );
  const displayedTotal = showActualized ? actualizedValue : directValue;
  const displayedChange = displayedTotal - baselineValue;
  const percentChange = hasScenario && (
    <div className='percent-change-container' style={{ width }}>
      <PercentChange
        stretchMode={StretchMode.FIT}
        negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
        percentChange={computeDeltaPercent(displayedChange, baselineValue, metric)}
        size={Size.SM}
      />
    </div>
  );
  const sparkChart = (
    <div className='spark-chart' style={{ width }}>
      <ComposedChart
        data={buildSparkChartData(flightingData, metric, useHistoricalFlighting)}
        height={SPARK_CHART_HEIGHT}
        margin={{
          top: SPARKCHART_LINE_WIDTH,
          right: 0,
          bottom: SPARKCHART_LINE_WIDTH,
          left: 0
        }}
        width={width}
      >
        <Line
          activeDot={false}
          dataKey={SPARK_CHART_DATA_KEY}
          dot={false}
          fillOpacity={1}
          isAnimationActive={false}
          name={metric}
          stroke={color}
          strokeWidth={SPARKCHART_LINE_WIDTH}
        />
        {!useHistoricalFlighting && <YAxis domain={[0, 2 * FLAT_LINE_VAL]} hide />}
      </ComposedChart>
    </div>
  );

  return (
    <foreignObject x={xPos} y={y} width={width} height={height}>
      <div className='tick-container' style={{ height, width }}>
        {textContainer}
        {percentChange}
        {sparkChart}
      </div>
    </foreignObject>
  );
};
